import './style.scss';
import React from 'react';
import { Button } from 'react-bootstrap';
import { Text } from '../../atoms';

export type EvaluationKnowledgeCardInfoData = {
    isKnowledgeEvaluation: boolean;
    hasFinishedEvaluation: boolean;
    showEvaluationRetry: boolean;
    evaluationAvailableAttempts: number;
    hasEvaluationAvailableAttempts: boolean;
    oneAvaliableAttempts: boolean;
    evaluationAttemptsMessage?: any;
    evaluationGradeMessage: string;
    evaluationConcluidedMessage?: any;
    evaluationQuestionnaireInit?: any;
};

const getEvaluationProgressTexts = (collection) => {
    if (!collection || collection.elementType !== 'EVALUATION' || collection.data.evaluationType !== 'KNOWLEDGE') {
        return null
    }

    const questionnaire = collection.data.questionnaire || null;
    const maxTries = Number(collection.data.evaluationMaxTries) || 1;

    if (!questionnaire) {
        return  `0 de ${maxTries} tentativa${maxTries > 1 ? 's' : ''}.`
    }

    const grade = Number(questionnaire?.grade ?? 0);
    const masteryScore = Number(collection.data.evaluationMasteryScore);
    const attempts = Number(collection.data.evaluationAvailableAttempts);
    const lockAfterApproval = Number(questionnaire?.lockAfterApproval || 0);
    const attemptsRemaining = attempts;
    const hasAttemptsRemaining = maxTries !== 1 && attempts < maxTries;
    const hasReachedMasteryScore = grade >= masteryScore;
    const lockAfterApprovalStatus = lockAfterApproval && hasReachedMasteryScore;

    let concluidedText = '';

    if (grade === null || grade === undefined || (!questionnaire?.finishDate && attempts !== 0)) {
        concluidedText = `0 de ${maxTries} tentativa${maxTries > 1 ? 's' : ''}`;
    } else {
        const resultText = hasReachedMasteryScore ? 'Nota necessária atingida.' : 'Nota necessária não atingida.';
        const attemptsText = hasAttemptsRemaining
            ? `Você tem ${attemptsRemaining} tentativa${attemptsRemaining > 1 ? 's' : ''} para melhorar a sua nota.`
            : 'Todas as tentativas foram utilizadas.';
        const progressStatusClass = hasReachedMasteryScore ? 'progressStatusSuccess' : 'progressStatusFailed';

        const gradeText = grade !== null && grade !== undefined ? `Nota: ${grade}. ` : '';
        concluidedText = `<span class='${progressStatusClass}'>${gradeText} ${resultText}</span>`;
    }

    return concluidedText
};

export const getEvaluationKnowledgeCardInfo = (collection: any): EvaluationKnowledgeCardInfoData => {
    const evaluationAvailableAttempts = Number(collection?.data?.evaluationAvailableAttempts || 0);
    const evaluationMaxTries = Number(collection?.data?.evaluationMaxTries || 0);
    const evaluationMasteryScore = Number(collection?.data?.evaluationMasteryScore || 0);
    const evaluationGrade = Number(collection?.data?.questionnaire?.grade || 0);
    const lockAfterApproval = Number(collection?.data?.questionnaire?.lockAfterApproval || 0);
    const evaluationMasteryScoreReached = evaluationGrade >= evaluationMasteryScore;
    const isKnowledgeEvaluation = collection?.elementType === 'EVALUATION' && collection?.data?.evaluationType === 'KNOWLEDGE';
    const hasFinishedEvaluation = Boolean(collection?.data?.questionnaire?.finishDate);
    const lockAfterApprovalStatus = lockAfterApproval && evaluationMasteryScoreReached;
    const oneAvaliableAttempts = evaluationAvailableAttempts === 1;
    
    const messageTotalAttempts =
        evaluationMaxTries === 1 ? (
            <>
                Você utilizou a <span>única tentativa</span> disponível para esta avaliação.
            </>
        ) : (
            <>
                Você utilizou todas as <span>{evaluationMaxTries} tentativas</span> disponíveis para esta avaliação.
            </>
        );

    const evaluationAttemptsMessage = () => {
        if (evaluationAvailableAttempts === 0) {
            return <>{messageTotalAttempts}</>;
        } else if (evaluationAvailableAttempts === 1) {
            return (
                <>
                    Você tem <span>1 tentativa</span> disponível.
                </>
            );
        } else if (evaluationAvailableAttempts > 1) {
            return (
                <>
                    Você tem <span>{evaluationAvailableAttempts} tentativas</span> disponíveis.
                </>
            );
        }

        return <></>;
    };
    const questionnaire  = collection?.data?.questionnaire || null;
    const grade = Number(questionnaire?.grade ?? 0);
    const attempts = Number(collection?.data?.evaluationAvailableAttempts);

    const evaluationQuestionnaireInit =
        !questionnaire || (!questionnaire?.finishDate && attempts !== 0);

    const showEvaluationRetry =
        isKnowledgeEvaluation &&
        evaluationMaxTries !== 1 &&
        evaluationAvailableAttempts > 0 &&
        evaluationMaxTries !== evaluationAvailableAttempts &&
        !lockAfterApprovalStatus;

    return {
        isKnowledgeEvaluation,
        hasFinishedEvaluation,
        showEvaluationRetry,
        evaluationAvailableAttempts,
        hasEvaluationAvailableAttempts: evaluationAvailableAttempts > 0,
        oneAvaliableAttempts: oneAvaliableAttempts,
        evaluationAttemptsMessage: evaluationAttemptsMessage(),
        evaluationGradeMessage: evaluationMasteryScoreReached
            ? `<span class="evaluationKnowledgeGradeReached">Nota: ${evaluationGrade}. Nota necessária atingida</span>`
            : `<span class="evaluationKnowledgeGradeNotReached">Nota: ${evaluationGrade}. Nota necessária não atingida</span>`,
        evaluationConcluidedMessage: getEvaluationProgressTexts(collection),
        evaluationQuestionnaireInit: !evaluationQuestionnaireInit || showEvaluationRetry,
    };
};

type EvaluationKnowledgeRetryProps = {
    info: EvaluationKnowledgeCardInfoData;
};

export const EvaluationKnowledgeRetry = ({ info }: EvaluationKnowledgeRetryProps) => {
    return (
        <div className="evaluationKnowledgeThumbContent">
            {(info.evaluationQuestionnaireInit || info.showEvaluationRetry) && (
                <>
                    <div className="evaluationKnowledgeAttemptsText">
                        {info.evaluationAttemptsMessage}
                    </div>
                </>
            )}
            {info.showEvaluationRetry && (
                <Button
                    className="evaluationKnowledgeRetryButton"
                    type="button"
                    disabled={!info.hasEvaluationAvailableAttempts}
                >
                    Tentar Novamente
                </Button>
            )}
        </div>
    );
};

type EvaluationKnowledgeGradeTextProps = {
    info: EvaluationKnowledgeCardInfoData;
    className?: string;
};

export const EvaluationKnowledgeGradeText = ({ info, className = '' }: EvaluationKnowledgeGradeTextProps) => {
    if (!info.isKnowledgeEvaluation) {
        return null;
    }

    return (
        <Text
            component="small"
            className={`evaluationKnowledgeGradeText ${className}`}
            text={info.evaluationGradeMessage}
        />
    );
};
